import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { UpgradeTopPeriod } from '../screens/UpgradeScreen/UpgradeTopSheet';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import { OrderEnum, PeriodEnum, StatusesEnum } from '../services/generated/Api';

const PAGE_SIZE = 20;
export const MIN_UPGRADE_ITEM_PRICE = 1000;
// WHY: потолок шанса 60% при комиссии 20% — вход выше 75% цены цели бэк отклонит
export const MAX_UPGRADE_ENTRY_SHARE = 0.75;
export const UPGRADE_FEE = 0.2;

export type UpgradeTargetsOrder = 'chance' | 'multiplier';

export interface UpgradeTargetsFilters {
  category?: string;
  weapon?: string;
  type?: string;
  searchTerm?: string;
  order?: UpgradeTargetsOrder;
  minProbability?: number;
  maxProbability?: number;
}

const ORDER: Record<UpgradeTargetsOrder, OrderEnum> = {
  chance: OrderEnum.Chance,
  multiplier: OrderEnum.Multiplier,
};

export function useUpgradeInventoryInfinite() {
  const apiClient = useApiClient();

  return useInfiniteQuery({
    queryKey: queryKeys.upgradeInventory(MIN_UPGRADE_ITEM_PRICE),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiClient.api.inventoryControllerGetInventory({
        statuses: [StatusesEnum.AVAILABLE],
        page: pageParam.toString(),
        limit: PAGE_SIZE.toString(),
        minPrice: MIN_UPGRADE_ITEM_PRICE,
      });
      return response;
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + 1 : undefined,
    initialPageParam: 1,
    staleTime: 0,
  });
}

export function useUpgradeTargetsInfinite(
  sourceItemId: string | null,
  sourceAmount: number | null,
  filters: UpgradeTargetsFilters,
) {
  const apiClient = useApiClient();
  const sourceKey = sourceItemId ? `${sourceItemId}${sourceAmount ? `+${sourceAmount}` : ''}` : '';
  const itemId = sourceItemId ?? '';

  return useInfiniteQuery({
    queryKey: queryKeys.upgradeTargets(sourceKey, { ...filters }),
    queryFn: async ({ pageParam = 1 }) =>
      apiClient.api.upgradeControllerGetAvailableUpgradeTargets({
        sourceItemId: itemId,
        sourceAmount: sourceAmount ?? undefined,
        page: pageParam,
        limit: PAGE_SIZE,
        category: filters.category,
        weapon: filters.weapon,
        type: filters.type,
        searchTerm: filters.searchTerm || undefined,
        minProbability: filters.minProbability,
        maxProbability: filters.maxProbability,
        order: filters.order ? ORDER[filters.order] : undefined,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage >= lastPage.meta.totalPages) return undefined;
      return lastPage.meta.currentPage + 1;
    },
    initialPageParam: 1,
    enabled: !!sourceKey,
    placeholderData: keepPreviousData,
  });
}

export function useUpgradeTop(period: UpgradeTopPeriod, enabled: boolean) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['upgrade', 'top', period],
    queryFn: () =>
      apiClient.api.upgradeControllerGetUpgradeTop({ period: period === 'day' ? PeriodEnum.Day : PeriodEnum.Month }),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useUpgradeHistoryInfinite(enabled: boolean) {
  const apiClient = useApiClient();

  return useInfiniteQuery({
    queryKey: queryKeys.upgradeHistory,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiClient.api.upgradeControllerGetUpgradeHistory({
        page: pageParam,
        perPage: PAGE_SIZE,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage >= lastPage.meta.totalPages) return undefined;
      return lastPage.meta.currentPage + 1;
    },
    initialPageParam: 1,
    enabled,
    staleTime: 0,
  });
}
